import { useMappedState } from "redux-react-hook";
import React, {
  useCallback,
  useMemo
} from "react";
import _ from "lodash";
import moment from "moment-timezone";

import { IStore } from "src/core/shared/store";
import { useDrop } from "src/core/drops/hooks";

import { IDrop } from "../../../core/drops/api/drops.api";
import {
  ShutdownDateInfo,
  formatShutdownDates
} from "../utils/formatShutdownDates";

/**
 * Returns an array of all shutdowns that apply to a drop
 * This will include both drop level and global shutdowns
 * */
export const useDropShutdowns = (inputDrop?: IDrop | null) => {
  const [ { drop } ] = useDrop();

  const dropToUse = React.useMemo(() => {
    if (inputDrop) {
      return inputDrop;
    }

    return drop;
  }, [ drop, inputDrop ]);

  const globalShutdowns = useMappedState(({ global }: IStore) => (global.settings?.shutdowns || null));

  // return an array of all shutdown dates, both drop and global
  const shutdownDates = useMemo(() => {
    let allShutdowns: ShutdownDateInfo[] = [];

    if (globalShutdowns) {
      allShutdowns = globalShutdowns;
    }

    if (dropToUse?.shutdowns?.length) {
      allShutdowns = _.concat(allShutdowns, formatShutdownDates(dropToUse.shutdowns));
    }

    return allShutdowns;
  }, [ dropToUse?.shutdowns, globalShutdowns ]);

  /**
   * Checks if a date matches shutdown and returns the details of the shutdown if it does
   */
  const checkShutdownDate = useCallback((date: Date, orderVariantId?: number) => {
    return _.find(shutdownDates, shutdownInfo => {
      // shutdown is exlcuded for this variant
      if (orderVariantId && shutdownInfo.shutdown.options?.excludeVariants?.length && shutdownInfo.shutdown.options.excludeVariants.includes(orderVariantId)) {
        return false;
      } else if (shutdownInfo.date === moment.utc(date).format("YYYY-MM-DD")) {
        return true;
      }

      return false;
    });
  }, [ shutdownDates ]);

  return {
    shutdownDates,
    checkShutdownDate
  };
};
